import { useEffect, useState } from "react";

import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { supabase } from "@/integrations/supabase/client";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { execute: invoke } = useEdgeFunction({ auth: true });

  const handleVerify = async () => {
    setStatus('verifying');
    const type = searchParams.get("type");
    const tokenHash = searchParams.get("token_hash");

    if (type === "signup" && tokenHash) {
      try {
        // eslint-disable-next-line no-restricted-properties
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: "signup",
        });

        if (error) {
          setError(error.message);
          toast.error("Failed to verify email. Please try again.");
          setStatus('error');
        } else {
          // Get current session to get user ID
          // eslint-disable-next-line no-restricted-properties
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.user) {
            // Mark user as verified and update queue
            const { data: verificationResult, error: verifyError } = await supabase.rpc(
              "mark_user_verified",
              { user_id: session.user.id }
            );

            if (verifyError) {
              console.error("Error updating verification status:", verifyError);
            } else if (verificationResult) {
              const result = verificationResult as any;
              
              // Send appropriate email based on role
              if (result.role === "waitlisted") {
                // Send thank you email
                await invoke("send-thank-you-email", {
                  body: {
                    email: session.user.email,
                    queuePosition: result.new_queue_position,
                    movedUp: result.moved_up || 0,
                  },
                });
              } else {
                // Send welcome email for non-waitlisted users
                await invoke("send-welcome-email", {
                  body: {
                    email: session.user.email,
                  },
                });
              }
            }
          }
          
          setStatus('success');
          toast.success("Email verified successfully!");
        }
      } catch (err) {
        setError("An unexpected error occurred");
        toast.error("An unexpected error occurred");
        setStatus('error');
      }
    } else {
      setError("Invalid verification link");
      setStatus('error');
    }
  };

  const handleContinue = () => {
    navigate("/");
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 flex flex-col items-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-center text-muted-foreground">Verifying your email...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          {status === 'idle' && (
            <>
              <div className="flex justify-center mb-2">
                <CheckCircle2 className="h-16 w-16 text-primary" />
              </div>
              <CardTitle className="text-2xl font-semibold">Verify Email</CardTitle>
              <CardDescription>
                Click the button below to verify your email address.
              </CardDescription>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="flex justify-center mb-2">
                <CheckCircle2 className="h-16 w-16 text-green-500" />
              </div>
              <CardTitle className="text-2xl font-semibold">Email Verified!</CardTitle>
              <CardDescription>
                Your email has been successfully verified. You're on our waitlist and will be notified once approved.
              </CardDescription>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="flex justify-center mb-2">
                <XCircle className="h-16 w-16 text-destructive" />
              </div>
              <CardTitle className="text-2xl font-semibold">Verification Failed</CardTitle>
              <CardDescription>
                {error || "We couldn't verify your email. The link may have expired or is invalid."}
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'idle' && (
            <Button onClick={handleVerify} className="w-full">
              Verify Email
            </Button>
          )}
          {status === 'success' && (
            <Button onClick={handleContinue} className="w-full">
              Continue to Dashboard
            </Button>
          )}
          {status === 'error' && (
            <Button onClick={handleContinue} className="w-full">
              Return to Sign In
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
